import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ChevronDown, ArrowRight, BedDouble, RefreshCcw, UserPlus, Calendar, Users, Moon, Mail, X } from "lucide-react";
import useDismissible from "@/lib/useDismissible";

import { toast } from "sonner";
import { errMsg } from "@/lib/errors";
import { formatDate } from "@/lib/dates";

const _str = (v) => (typeof v === "string" ? v : v?.it || v?.en || (v && Object.values(v)[0]) || "");

/**
 * Banner that surfaces walk-in check-ins arrived from the Guide App that still
 * need a room assignment. These bookings have `room_id="tbd_walkin"` so they
 * never appear on a room-specific calendar — admin would otherwise miss them.
 *
 * Shows on the calendar pages (room calendar, PMS panoramica) with one-click
 * navigation to the Check-in tab where the admin can assign a room.
 *
 * Props:
 *   - onAssignClick(booking): callback to open the assignment flow (optional);
 *     if not provided, navigates to /admin/dashboard?tab=checkins
 */
export default function UnassignedWalkinsBanner({ onAssignClick }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  // iter190 — X per chiudere per 3 giorni (breve perché sono check-in urgenti)
  const [dismissed, dismiss] = useDismissible("unassigned-walkins", 3);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/bookings/walk-in/pending");
      setItems(r.data?.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  if (items.length === 0) return null;
  if (dismissed) return null;

  const handleAssign = (b) => {
    if (onAssignClick) {
      onAssignClick(b);
    } else {
      window.location.href = "/admin/dashboard?tab=checkins";
    }
  };

  /**
   * Reverse-sync: ask the Guide app for all bookings and apply room-number
   * assignments that exist there but not locally. Useful when the admin
   * assigns a room inside the Guide app but the `booking.updated` webhook
   * never reached us (or was lost).
   */
  const handleSyncAssignments = async () => {
    setLoading(true);
    try {
      const r = await api.post("/admin/guide/pull-room-assignments");
      const d = r.data || {};
      if (d.updated > 0) {
        toast.success(`✓ ${d.updated} camere sincronizzate dall'App Guida`);
      } else if (d.guide_total === 0) {
        toast.info("L'App Guida non ha prenotazioni da leggere");
      } else {
        toast.info("Nessuna camera da sincronizzare: tutto già allineato");
      }
      load();
    } catch (e) {
      toast.error(errMsg(e, "Errore sync dall'App Guida"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-amber-50 border border-amber-300 rounded-xl overflow-hidden relative" data-testid="unassigned-walkins-banner">
      {/* iter190 — X per chiudere il banner per 3 giorni */}
      <button
        onClick={(e) => { e.stopPropagation(); dismiss(); }}
        className="absolute top-1.5 right-1.5 p-1 rounded hover:bg-amber-200 text-amber-800/70 hover:text-amber-900 transition-colors z-10"
        aria-label="Chiudi per 3 giorni"
        title="Nascondi per 3 giorni"
        data-testid="unassigned-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-amber-100/50 transition"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-full bg-amber-200 text-amber-800 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-amber-900 flex items-center gap-2 flex-wrap">
              Check-in dall'App Guida senza camera assegnata
              <span className="bg-amber-600 text-white text-xs font-bold rounded-full px-2 py-0.5">{items.length}</span>
            </div>
            <p className="text-xs text-amber-800 mt-0.5">
              L'App Guida non comunica la camera — assegnala qui per farli apparire nei calendari.
            </p>
          </div>
        </div>
        <ChevronDown className={`w-5 h-5 text-amber-700 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 border-t border-amber-200 pt-3 space-y-2">
          <div className="flex items-center justify-between gap-2 mb-1">
            <p className="text-xs text-amber-800">
              💡 Hai già assegnato la camera sull'App Guida? Clicca per sincronizzare.
            </p>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="bg-white border-amber-400 text-amber-800 hover:bg-amber-100 h-7 text-xs"
              onClick={handleSyncAssignments}
              disabled={loading}
              data-testid="unassigned-sync-from-guide"
            >
              <RefreshCcw className={`w-3 h-3 mr-1 ${loading ? "animate-spin" : ""}`} />
              Sincronizza dalla Guida
            </Button>
          </div>
          {items.map((b) => {
            const g = b.guest || {};
            const name = [g.first_name, g.last_name].filter(Boolean).join(" ") || "Ospite senza nome";
            const stay = b.check_in && b.check_out ? `${formatDate(b.check_in)} → ${formatDate(b.check_out)}` : "—";
            return (
              <div
                key={b.id}
                className="bg-white border border-amber-200 rounded-lg p-3 flex flex-wrap items-center gap-2"
                data-testid={`unassigned-walkin-${b.code}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <UserPlus className="w-4 h-4 text-amber-700 shrink-0" />
                    <span className="font-semibold text-[#1E3A5F]">{name}</span>
                    <span className="text-[10px] font-mono bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">{b.code}</span>
                  </div>
                  <div className="text-xs text-[#635F59] mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                    <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {stay}</span>
                    <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {b.guests || 1} ospit{(b.guests || 1) === 1 ? "e" : "i"}</span>
                    <span className="inline-flex items-center gap-1"><Moon className="w-3 h-3" /> {b.nights || 1} nott{(b.nights || 1) === 1 ? "e" : "i"}</span>
                    {g.email && <span className="inline-flex items-center gap-1 truncate max-w-[200px]"><Mail className="w-3 h-3" /> {g.email}</span>}
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => handleAssign(b)}
                  className="bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold"
                  data-testid={`unassigned-walkin-assign-${b.code}`}
                >
                  <BedDouble className="w-3 h-3 mr-1" />
                  Assegna camera
                  <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
            );
          })}
          <Button
            onClick={load}
            disabled={loading}
            variant="outline"
            size="sm"
            className="text-xs w-full mt-2"
            data-testid="unassigned-walkins-refresh"
          >
            <RefreshCcw className={`w-3 h-3 mr-1 ${loading ? "animate-spin" : ""}`} /> Aggiorna
          </Button>
        </div>
      )}
    </div>
  );
}
